/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * App selectors
 *
 * @author Mikhail Kokoulin
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.0.4
 */
import get from 'lodash/get';
import { createSelector, OutputSelector } from 'reselect';

import type { IRootReducer } from '../../reducers/rootReducer';
import type { IAppFeature, IAppReducerState, IAuthSSO } from './appTypes';
import { EAppFeatures } from './appTypes';

/**
 * To state from the global state of app
 * @param state - root state
 */
export const selectApp = (state: IRootReducer): IAppReducerState =>
  state?.app || {};

/**
 * Receive isLoading
 */
export const makeSelectIsAppLoading = (): OutputSelector<
  IRootReducer,
  boolean,
  (state: IAppReducerState) => boolean
> => createSelector(selectApp, (state: IAppReducerState) => state.isLoading);

export const makeSelectIsAppLoaded = (): OutputSelector<
  IRootReducer,
  boolean,
  (state: IAppReducerState) => boolean
> => createSelector(selectApp, (state: IAppReducerState) => state.isLoaded);

/**
 * Receive gtmContainerId
 * @returns {OutputSelector<IRootReducer, string, {}>}
 */
export const makeSelectGtmContainerId = (): OutputSelector<
  IRootReducer,
  string,
  (state: IAppReducerState) => string
> =>
  createSelector(
    selectApp,
    (state: IAppReducerState) => state.config?.gtmContainerId ?? null,
  );

/**
 * Receive gtmEnabled
 * @returns {OutputSelector<IRootReducer, boolean, {}>}
 */
export const makeSelectIsGtmEnabled = (): OutputSelector<
  IRootReducer,
  boolean,
  (state: IAppReducerState) => boolean
> =>
  createSelector(
    selectApp,
    (state: IAppReducerState) => state.config?.gtmEnabled ?? null,
  );

export const makeSelectOperatorSettings = (): OutputSelector<
  IRootReducer,
  object,
  (state: IAppReducerState) => object | null
> =>
  createSelector(selectApp, (state: IAppReducerState) =>
    get(state, 'config.operatorSettings', null),
  );

/*
 * -------------------------------------------
 *           APP ERROR
 * -------------------------------------------
 */

export const makeSelectAppHasError = (): OutputSelector<
  IRootReducer,
  boolean,
  (state: IAppReducerState) => boolean
> => createSelector(selectApp, (state: IAppReducerState) => state.hasError);

export const makeSelectAppHasInitError = (): OutputSelector<
  IRootReducer,
  boolean,
  (state: IAppReducerState) => boolean
> => createSelector(selectApp, (state: IAppReducerState) => state.hasInitError);

export const makeSelectAppErrorCode = (): OutputSelector<
  IRootReducer,
  string,
  (state: IAppReducerState) => string
> => createSelector(selectApp, (state: IAppReducerState) => state.code);

export const makeSelectAuthSSO = (): OutputSelector<
  IRootReducer,
  IAuthSSO,
  (state: IAppReducerState) => IAuthSSO
> => createSelector(selectApp, (state) => state.config?.sso || {});

/*
 * -------------------------------------------
 *           Service access
 * -------------------------------------------
 */

export const makeSelectAppFeatureEnabled = (
  featureName: EAppFeatures,
): OutputSelector<
  IRootReducer,
  boolean | undefined,
  (state: IAppReducerState) => boolean | undefined
> =>
  createSelector(
    selectApp,
    (state: IAppReducerState) =>
      state.config.features?.find(
        (feature: IAppFeature) => feature.name === featureName,
      )?.enabled,
  );
